import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { theme } from '../../../styles/theme';
import { AdminContainer } from '../AdminShell';

type Enrollment = {
  id: string;
  customer_id: string;
  sequence_id: string;
  current_step: number;
  status: string;
  enrolled_at: string;
  next_run_at: string | null;
  completed_at: string | null;
  customer?: { business_name: string; primary_email: string };
  sequence?: { code: string; name: string };
};

type Sequence = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  trigger_type: string;
  trigger_days: number | null;
  active: boolean;
};

type LeadEvent = {
  id: string;
  channel: string;
  subject: string | null;
  body: string;
  status: string;
  created_at: string;
  customer?: { business_name: string };
};

export function AdminNurture() {
  const [tab, setTab] = useState<'enrollments' | 'events' | 'sequences'>('enrollments');
  const [sequences, setSequences] = useState<Sequence[]>([]);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [events, setEvents] = useState<LeadEvent[]>([]);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    loadSequences();
    loadEnrollments();
    loadEvents();
  }, []);

  async function loadSequences() {
    const { data } = await supabase.from('nurture_sequences')
      .select('*').order('created_at', { ascending: false });
    setSequences((data as any) ?? []);
  }
  async function loadEnrollments() {
    const { data } = await supabase.from('nurture_enrollments')
      .select('id, customer_id, sequence_id, current_step, status, enrolled_at, next_run_at, completed_at, customer:customers(business_name, primary_email), sequence:nurture_sequences(code, name)')
      .order('enrolled_at', { ascending: false }).limit(100);
    setEnrollments((data as any) ?? []);
  }
  async function loadEvents() {
    const { data } = await supabase.from('nurture_lead_events')
      .select('id, channel, subject, body, status, created_at, customer:customers(business_name)')
      .order('created_at', { ascending: false }).limit(50);
    setEvents((data as any) ?? []);
  }

  async function runWorker() {
    setRunning(true);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/nurture-worker`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token ?? ''}` },
    });
    const data = await res.json();
    alert(`Worker corrió: ${JSON.stringify(data.stats)}`);
    setRunning(false);
    loadEnrollments();
    loadEvents();
  }

  return (
    <AdminContainer
      eyebrow="Marketing & Ventas"
      title="Lead Nurture"
      action={
        <button onClick={runWorker} disabled={running} style={{
          background: theme.accent, color: theme.brand, border: 'none',
          padding: '10px 18px', fontSize: 12, fontWeight: 700,
          textTransform: 'uppercase', cursor: 'pointer', fontFamily: theme.font.sans,
          opacity: running ? 0.5 : 1,
        }}>
          {running ? 'Corriendo…' : '▶ Correr worker ahora'}
        </button>
      }
    >
      <div style={{ marginBottom: 16, padding: 12, background: '#FEF3C7', border: '1px solid #F59E0B', fontSize: 12, color: theme.brand }}>
        <strong>⚠️ Modo DRY-RUN:</strong> El worker registra los mensajes que MANDARÍA pero NO envía emails ni WhatsApps reales todavía.
        Para activar envíos reales: integrar Resend API key + conectar WhatsApp Cloud API.
      </div>

      <div style={{ display: 'inline-flex', background: theme.bg, border: `1px solid ${theme.border}`, marginBottom: 20 }}>
        <SubBtn active={tab === 'enrollments'} onClick={() => setTab('enrollments')}>
          📋 Inscritos ({enrollments.length})
        </SubBtn>
        <SubBtn active={tab === 'events'} onClick={() => setTab('events')}>
          📨 Mensajes generados ({events.length})
        </SubBtn>
        <SubBtn active={tab === 'sequences'} onClick={() => setTab('sequences')}>
          🔁 Secuencias ({sequences.length})
        </SubBtn>
      </div>

      {tab === 'enrollments' && (
        <div style={{ background: theme.bg, border: `1px solid ${theme.border}`, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: theme.bgSoft }}>
                <Th>Cliente</Th>
                <Th>Secuencia</Th>
                <Th style={{ textAlign: 'center' }}>Paso</Th>
                <Th>Próximo</Th>
                <Th>Estado</Th>
              </tr>
            </thead>
            <tbody>
              {enrollments.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center', color: theme.textMuted }}>
                  Sin inscritos. El worker corre diario 09:00 CDMX y enrolla clientes dormidos 60+ días.
                </td></tr>
              ) : enrollments.map(e => (
                <tr key={e.id} style={{ borderBottom: `1px solid ${theme.border}` }}>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{e.customer?.business_name ?? '—'}</div>
                    <div style={{ fontSize: 10, color: theme.textMuted }}>{e.customer?.primary_email ?? '—'}</div>
                  </Td>
                  <Td><span style={{ fontFamily: theme.font.mono, fontSize: 11 }}>{e.sequence?.code}</span></Td>
                  <Td style={{ textAlign: 'center' }}>{e.current_step}</Td>
                  <Td>
                    {e.next_run_at ? new Date(e.next_run_at).toLocaleDateString('es-MX') : '—'}
                  </Td>
                  <Td><StatusTag s={e.status} /></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'events' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {events.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: theme.textMuted, background: theme.bg, border: `1px solid ${theme.border}` }}>
              Sin mensajes generados aún.
            </div>
          ) : events.map(ev => (
            <div key={ev.id} style={{ background: theme.bg, border: `1px solid ${theme.border}`, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <div style={{ fontWeight: 600, color: theme.brand, fontSize: 13 }}>
                  {ev.channel === 'email' ? '📧' : ev.channel === 'whatsapp' ? '💬' : '📨'} {ev.customer?.business_name ?? '—'}
                </div>
                <span style={{ fontSize: 10, color: theme.textMuted }}>
                  {new Date(ev.created_at).toLocaleString('es-MX')} · {ev.status}
                </span>
              </div>
              {ev.subject && (
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Subj: {ev.subject}</div>
              )}
              <pre style={{ fontSize: 11, color: theme.text, whiteSpace: 'pre-wrap', fontFamily: theme.font.sans, margin: 0 }}>
                {ev.body.slice(0, 300)}{ev.body.length > 300 ? '…' : ''}
              </pre>
            </div>
          ))}
        </div>
      )}

      {tab === 'sequences' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {sequences.map(s => (
            <div key={s.id} style={{ background: theme.bg, border: `1px solid ${theme.border}`, padding: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontFamily: theme.font.mono, fontSize: 10, color: theme.textMuted }}>{s.code}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: theme.brand }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 4 }}>{s.description}</div>
                </div>
                <div style={{ fontSize: 11, color: s.active ? '#10B981' : '#DC2626', fontWeight: 700 }}>
                  {s.active ? '● ACTIVA' : '○ INACTIVA'}
                </div>
              </div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 6 }}>
                Trigger: <strong>{s.trigger_type}</strong> · {s.trigger_days} días
              </div>
            </div>
          ))}
        </div>
      )}
    </AdminContainer>
  );
}

function Th({ children, style }: { children: any; style?: React.CSSProperties }) {
  return (
    <th style={{
      padding: '10px 12px', textAlign: 'left', borderBottom: `1px solid ${theme.border}`,
      fontSize: 10, color: theme.textMuted, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4,
      ...style,
    }}>{children}</th>
  );
}
function Td({ children, style }: { children: any; style?: React.CSSProperties }) {
  return <td style={{ padding: '10px 12px', verticalAlign: 'top', ...style }}>{children}</td>;
}
function SubBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: any }) {
  return (
    <button onClick={onClick} style={{
      background: active ? theme.brand : 'transparent',
      color: active ? theme.textOnBrand : theme.brand,
      border: 'none', padding: '10px 16px', fontSize: 12, fontWeight: 600,
      cursor: 'pointer', fontFamily: theme.font.sans,
    }}>{children}</button>
  );
}
function StatusTag({ s }: { s: string }) {
  const map: Record<string, string> = {
    active: '#10B981', paused: '#F59E0B', completed: '#6B7280', cancelled: '#DC2626',
  };
  return (
    <span style={{
      background: map[s] || '#6B7280', color: 'white',
      padding: '3px 7px', fontSize: 10, fontWeight: 700,
      letterSpacing: 0.4, textTransform: 'uppercase', borderRadius: 3,
    }}>{s}</span>
  );
}
